import { useGet, usePost } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import type { ListRequestsItem } from "@albertine/types/openapi/lmt";
import { Lib } from "albertine-shared-web";
import { useState } from "react";
import { memberRequestsQueryParams } from "../../common/hooks/useMemberRequests";
import type { Message, MessageRequest } from "../../common/types/Message";
import { defaultRequestTitle, type Request } from "../../common/types/Request";
import { moveMatchingItemToFront } from "../../common/utils/array.util";
import { getSenderRole } from "../../common/utils/message.util";
import { localDateToDayDateString } from "../../utils/time.util";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ChatMessageBubble from "./ChatMessageBubble";
import ChatMessageInner from "./ChatMessageInner";
import "./ChangeMessageRequest.css";

type Topic = MessageRequest & {
    desc: string;
};

const generalChat: Topic = {
    request: "general-chat",
    requestTitle: "General",
    desc: "No topic",
};

function requestToTopic(item: ListRequestsItem): Topic {
    return {
        request: item.id,
        requestTitle: item.title ?? null,
        desc: item.startDate
            ? localDateToDayDateString(item.startDate)
            : "(no start date)",
    };
}

/** Ascending by start date; undated requests last (matches sortRequestsByEventDate). */
function sortByStartDate(a: ListRequestsItem, b: ListRequestsItem): number {
    return Lib.Utils.Sorting.sortByDateComparator<ListRequestsItem>((r) => {
        const dateStr = r?.startDateTime ?? r?.startDate ?? undefined;
        return dateStr ? new Date(dateStr) : undefined;
    })(a, b);
}

function groupRequestsIntoTopics(items: ListRequestsItem[]): {
    active: Topic[];
    confirmed: Topic[];
    closed: Topic[];
} {
    return {
        active: [...items]
            .filter((it) => it.status !== "closed" && it.status !== "confirmed")
            .sort(sortByStartDate)
            .map(requestToTopic),
        confirmed: [...items]
            .filter((it) => it.status === "confirmed")
            .sort(sortByStartDate)
            .map(requestToTopic),
        closed: [...items]
            .filter((it) => it.status === "closed")
            .sort((a, b) => -sortByStartDate(a, b))
            .map(requestToTopic),
    };
}

function fullRequestToTopic(req: Request): Topic {
    return {
        request: req.id,
        requestTitle: req.title ?? null,
        desc: req.startDate
            ? localDateToDayDateString(req.startDate)
            : "(no start date)",
    };
}

function getTopic(message: Message): Topic {
    const { request, requestTitle } = message;
    return message.request ? { request, requestTitle, desc: "" } : generalChat;
}

/** How many message bubbles to preview before collapsing into a "+N more" line. */
const MAX_PREVIEW_MESSAGES = 3;

type ChangeMessageRequestProperties = {
    member: MemberDataFirestoreRecord;
    conversationId: string;
    close: () => void;
    messages: Message[];
    currentRequest?: Request | undefined;
    onApplied?: () => void;
};

function isInternalNote(message: Message): boolean {
    return message.isInternalNote === true;
}

function TopicSelection(props: {
    topics: Topic[];
    selectedTopic: Topic;
    disabled: boolean;
    onSelect: (selectedTopic: Topic) => Promise<void>;
    title?: string;
}) {
    const { disabled, title, topics, selectedTopic, onSelect } = props;

    return topics.length !== 0 ? (
        <>
            {title && (
                <div className="change-message-request__action__sub-cta">{title}</div>
            )}
            <ul>
                {topics.map((topic) => (
                    <li key={topic.request}>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={async () => {
                                await onSelect(topic);
                            }}
                        >
                            <input
                                type="radio"
                                checked={topic.request === selectedTopic.request}
                                onChange={() => {}}
                            />
                            {topic.requestTitle || defaultRequestTitle}
                            <span className="description">
                                <InlineTextDelimiter />
                                {topic.desc}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </>
    ) : null;
}

function ChangeMessageRequest(props: ChangeMessageRequestProperties) {
    const { messages, conversationId, member, currentRequest, onApplied, close } =
        props;
    const [saving, setSaving] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState<Topic>(getTopic(messages[0]));
    const memberId = member.id;

    const { data: memberRequestsData } = useGet(
        "requests",
        memberRequestsQueryParams(memberId),
    );
    const { mutateAsync: assignConversationItemsToRequest } = usePost(
        "assignConversationItemsToRequest",
    );

    const topics = memberRequestsData
        ? groupRequestsIntoTopics(memberRequestsData.requests)
        : { active: [] as Topic[], confirmed: [] as Topic[], closed: [] as Topic[] };

    const onSelect = async (topic: Topic) => {
        if (selectedTopic.request === topic.request) return;
        setSaving(true);
        try {
            await assignConversationItemsToRequest({
                param: { conversationId },
                json: {
                    requestId:
                        topic.request === generalChat.request
                            ? null
                            : (topic.request ?? null),
                    messageIds: messages
                        .filter((target) => !isInternalNote(target))
                        .map((target) => target.id),
                    noteIds: messages
                        .filter((target) => isInternalNote(target))
                        .map((target) => target.id),
                },
            });
            setSelectedTopic(topic);
            onApplied?.();
        } catch {
            // Keep the previous topic selected when the assign fails.
        } finally {
            setSaving(false);
        }
    };

    const currentTopic = currentRequest
        ? fullRequestToTopic(currentRequest)
        : undefined;

    const activeTopics: Topic[] = [generalChat, ...topics.active];

    if (
        currentTopic &&
        !activeTopics.some((it) => it.request === currentTopic.request)
    ) {
        activeTopics.unshift(currentTopic);
    }

    const topicsEqual = (a: Topic, b: Topic) => a.request === b.request;
    const notCurrentTopic = (it: Topic): boolean =>
        currentTopic ? !topicsEqual(it, currentTopic) : true;
    const activeTopicsToDisplay = currentTopic
        ? moveMatchingItemToFront(currentTopic, activeTopics, topicsEqual)
        : activeTopics;

    const previewMessages = messages.slice(0, MAX_PREVIEW_MESSAGES);
    const hiddenPreviewCount = messages.length - previewMessages.length;

    return (
        <div className="change-message-request">
            {previewMessages.map((previewMessage) => (
                <div
                    key={previewMessage.id}
                    className="change-message-request__message"
                    onClick={close}
                >
                    <ChatMessageInner
                        sender={getSenderRole(previewMessage)}
                        messageBase={previewMessage}
                    >
                        <ChatMessageBubble
                            message={previewMessage}
                            sender={getSenderRole(previewMessage)}
                            currentMember={member}
                        />
                    </ChatMessageInner>
                </div>
            ))}
            {hiddenPreviewCount > 0 && (
                <div className="mt-2 text-desktop-paragraph-xsmall text-tertiary">
                    +{hiddenPreviewCount} more
                </div>
            )}
            <div className="change-message-request__action">
                <div className="change-message-request__action__cta">
                    <span className="change-message-request__action__cta">
                        {messages.length > 1
                            ? `Choose a topic for ${messages.length} messages`
                            : "Choose a topic"}
                    </span>
                    {saving && <LoadingSpinner />}
                </div>
                <div className="change-message-request__topics">
                    <TopicSelection
                        disabled={saving}
                        topics={activeTopicsToDisplay}
                        selectedTopic={selectedTopic}
                        onSelect={onSelect}
                    />

                    <TopicSelection
                        title="Confirmed"
                        disabled={saving}
                        topics={topics.confirmed.filter(notCurrentTopic)}
                        selectedTopic={selectedTopic}
                        onSelect={onSelect}
                    />

                    <TopicSelection
                        title="Closed"
                        disabled={saving}
                        topics={topics.closed.filter(notCurrentTopic)}
                        selectedTopic={selectedTopic}
                        onSelect={onSelect}
                    />
                </div>
            </div>
        </div>
    );
}

export default ChangeMessageRequest;
